/**
 * La citerne comme unité de compte du volume.
 *
 * « 30 citernes » veut dire 30 × un volume unitaire de référence, réglé par la
 * société (`/org-settings/volume-units`). La vérité reste toujours les litres :
 * ces fonctions sont **de présentation**. La seule exception est le champ de
 * saisie, qui convertit une fois, avant l'appel API.
 *
 * Ne jamais stocker les deux nombres pour une commande saisie en litres : deux
 * valeurs décrivant le même volume finissent par diverger, et plus personne ne
 * sait laquelle fait foi.
 */

import { formatNumber, formatVolume } from './utils';

// Repli quand la société n'a rien réglé — même valeur que le défaut serveur.
export const DEFAULT_CISTERN_LITERS = 40000;
export const DEFAULT_CISTERN_LABEL = 'citerne';

export const UNIT_LITERS = 'L';
export const UNIT_CISTERNS = 'CISTERN';

/** Volume unitaire effectif d'un réglage société, avec repli sûr. */
export const cisternLitersOf = (settings) => {
  const value = Number(
    settings?.cistern_liters ?? settings?.settings?.cistern_liters,
  );
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_CISTERN_LITERS;
};

export const cisternLabelOf = (settings) =>
  settings?.cistern_label ??
  settings?.settings?.cistern_label ??
  DEFAULT_CISTERN_LABEL;

/** Citernes → litres. Utilisé à la saisie, avant l'envoi. */
export const litersFromCisterns = (cisterns, cisternLiters) => {
  const count = Number(cisterns);
  const unit = Number(cisternLiters);
  if (!Number.isFinite(count) || !Number.isFinite(unit) || unit <= 0) return 0;
  return count * unit;
};

/** Litres → citernes. Purement d'affichage : jamais persisté. */
export const cisternsFromLiters = (liters, cisternLiters) => {
  const volume = Number(liters);
  const unit = Number(cisternLiters);
  if (!Number.isFinite(volume) || !Number.isFinite(unit) || unit <= 0) return 0;
  return volume / unit;
};

/**
 * Un volume dans l'unité demandée, toujours lisible.
 * `unit` vaut UNIT_LITERS (défaut) ou UNIT_CISTERNS.
 */
export const formatVolumeWithUnit = (liters, settings, unit = UNIT_LITERS) => {
  if (liters === null || liters === undefined || liters === '') return '-';
  if (unit !== UNIT_CISTERNS) return formatVolume(liters);

  const count = cisternsFromLiters(liters, cisternLitersOf(settings));
  const label = cisternLabelOf(settings);
  // Les décimales ne sont montrées que si elles existent : « 30 citernes » se
  // lit mieux que « 30,00 citernes », et « 0,75 » reste indispensable.
  const rounded = Math.round(count * 100) / 100;
  const plural = Math.abs(rounded) >= 2 ? 's' : '';
  return `${formatNumber(rounded)} ${label}${plural}`;
};

/** L'équivalent dans l'autre unité, à afficher sous le champ de saisie. */
export const volumeEquivalentLabel = (liters, settings, unit = UNIT_LITERS) =>
  unit === UNIT_CISTERNS
    ? formatVolumeWithUnit(liters, settings, UNIT_LITERS)
    : formatVolumeWithUnit(liters, settings, UNIT_CISTERNS);
